import {
    FirebaseApp,
    FirebaseOptions,
    initializeApp as fInitializeApp,
} from 'firebase/app';
import {
    Firestore,
    FirestoreSettings,
    initializeFirestore,
} from 'firebase/firestore';
import axios from 'axios';
import {
    AppCheck,
    initializeAppCheck,
    CustomProvider,
    AppCheckToken,
} from 'firebase/app-check';
import { isProductionEnv } from '../../utils/environmentUtil';

// Extend the Window interface to include 'turnstile'
declare global {
    interface Window {
        turnstile: any;
    }
}

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let recaptchaKey: string;
let appCheck: AppCheck | null = null;

async function acquireAppCheckToken(
    captchaToken: string,
    appId: string | undefined,
): Promise<AppCheckToken> {
    try {
        if (!appId) {
            throw new Error('Firebase App ID is not defined.');
        }
        const baseUrl = isProductionEnv()
            ? 'https://us-central1-phio-main.cloudfunctions.net'
            : 'https://us-central1-phio-dev-c5d2d.cloudfunctions.net';
        const resp = await axios.post(`${baseUrl}/tkacquisition-acquiretoken`, {
            data: {
                appId, // Send the Firebase App ID to the Cloud Function
                altCaptchaToken: captchaToken, // Use the token from the alternative CAPTCHA service
            },
        });
        return resp.data.result;
    } catch (err) {
        console.error('Error fetching App Check token:', err);
        throw new Error(`Failed to fetch App Check token: ${err}`);
    }
}

const customAppcheckProvider = new CustomProvider({
    getToken: async () => {
        // get from the last state of turnstile
        // omitting the ID so that we can retrieve the last token
        const turnstileToken = await window.turnstile.getResponse();
        if (!turnstileToken) {
            throw new Error('Failed to get alternative CAPTCHA token.');
        }
        const appCheckToken = await acquireAppCheckToken(
            turnstileToken,
            app?.options.appId,
        );
        return {
            token: appCheckToken.token,
            expireTimeMillis: Date.now() + 10 * 60 * 1000, // 10 minutes
        };
    },
});

export const initializeApp = (
    options: FirebaseOptions & { recaptchaKey: string },
) => {
    recaptchaKey = options.recaptchaKey;
    app = fInitializeApp(options);

    appCheck = initializeAppCheck(app, {
        provider: customAppcheckProvider,
        isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
    });
    return app;
};

export const getFirestoreInstance = (databaseId?: string) => {
    if (app == null) {
        throw new Error('Firebase not initialised');
    }
    if (db == null) {
        const firestoreSettings: FirestoreSettings & {
            useFetchStreams: boolean;
        } = {
            useFetchStreams: false,
            experimentalForceLongPolling: true,
        };

        db = initializeFirestore(app, firestoreSettings, databaseId);
    }

    return db;
};

export const getAppCheck = () => {
    if (app == null) {
        throw new Error('Firebase not initialised');
    }
    if (!recaptchaKey) {
        throw new Error('Recaptcha key not found');
    }
    if (appCheck == null) {
        appCheck = initializeAppCheck(app, {
            provider: customAppcheckProvider,
            isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
        });
        return appCheck;
    }
    return appCheck;
};
